/**
 * Card Match lobby scene.
 *
 * Shown when no round is active. Displays the brand title, a "How to play"
 * panel, and the cross-game leaderboard. Mirrors the WhatIs/Anagram lobby
 * layout for visual consistency.
 */

import type { LbRow } from '@sg/shared';

export interface LobbyHandle {
  setVisible(v: boolean): void;
  setLeaderboard(top: LbRow[], allTime: LbRow[]): void;
  dispose(): void;
}

const ROOT_ID = 'cm-lobby';

export function createLobby(stage: HTMLElement): LobbyHandle {
  ensureStyles();
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('data-overlay', 'lobby');

  root.innerHTML = `
    <div class="cm-lobby-grid">
      <header class="cm-lobby-header">
        <span class="cm-lobby-eyebrow">CHAT GAME</span>
        <h1 class="cm-lobby-title">CARD MATCH</h1>
        <p class="cm-lobby-tagline">Two numbers. One pair. Type to flip.</p>
      </header>

      <section class="cm-lobby-howto">
        <h2>HOW TO PLAY</h2>
        <ol>
          <li>Streamer types <code>!start</code></li>
          <li>Type two grid numbers in chat — e.g. <code>3 7</code> or <code>3,7</code></li>
          <li>Match a pair to score; faster matches earn more points</li>
          <li>Match the whole board to win the round</li>
        </ol>
      </section>

      <section class="cm-lobby-leaderboard">
        <h2>LEADERBOARD</h2>
        <ol class="cm-lb-list" data-cm-lb></ol>
      </section>
    </div>
  `;

  stage.appendChild(root);

  function setVisible(v: boolean): void {
    root.style.display = v ? '' : 'none';
  }

  function setLeaderboard(top: LbRow[], allTime: LbRow[]): void {
    const list = root.querySelector('[data-cm-lb]');
    if (!list) return;
    const rows = (top.length > 0 ? top : allTime).slice(0, 8);
    list.innerHTML = rows
      .map(
        (r, i) => `
          <li class="cm-lb-row">
            <span class="cm-lb-rank">${i + 1}</span>
            <span class="cm-lb-name">${escapeHtml(r.display_name)}</span>
            <span class="cm-lb-pts">${formatPoints(r.total_points)}</span>
          </li>
        `,
      )
      .join('');
  }

  return {
    setVisible,
    setLeaderboard,
    dispose() {
      root.remove();
    },
  };
}

function ensureStyles(): void {
  if (document.getElementById('cm-lobby-style')) return;
  const s = document.createElement('style');
  s.id = 'cm-lobby-style';
  s.textContent = `
    #${ROOT_ID} {
      position: absolute;
      inset: 0;
      display: block;
    }
    #${ROOT_ID} .cm-lobby-grid {
      display: grid;
      grid-template-columns: 1.4fr 1fr;
      grid-template-rows: auto 1fr;
      gap: 32px;
      padding: 80px 100px;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
    #${ROOT_ID} .cm-lobby-header {
      grid-column: 1 / -1;
      text-align: center;
    }
    #${ROOT_ID} .cm-lobby-eyebrow {
      display: inline-block;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 28px;
      letter-spacing: 6px;
      color: var(--sg-secondary, #06B6D4);
      padding: 6px 16px;
      border: 2px solid var(--sg-secondary, #06B6D4);
      border-radius: 4px;
      margin-bottom: 24px;
    }
    #${ROOT_ID} .cm-lobby-title {
      font-family: 'Bebas Neue', 'Oswald', sans-serif;
      font-size: 220px;
      font-weight: 700;
      letter-spacing: 12px;
      line-height: 0.9;
      margin: 0;
      background: linear-gradient(180deg,
        var(--sg-primary, #A855F7) 0%,
        var(--sg-accent, #EC4899) 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      text-shadow: 0 0 80px rgba(168, 85, 247, 0.4);
    }
    #${ROOT_ID} .cm-lobby-tagline {
      font-family: 'Inter', sans-serif;
      font-size: 32px;
      letter-spacing: 4px;
      color: rgba(255, 255, 255, 0.85);
      margin: 24px 0 0;
    }
    #${ROOT_ID} .cm-lobby-howto, #${ROOT_ID} .cm-lobby-leaderboard {
      background: rgba(31, 27, 75, 0.5);
      border: 1px solid rgba(168, 85, 247, 0.3);
      border-radius: 16px;
      padding: 32px 40px;
      backdrop-filter: blur(8px);
    }
    #${ROOT_ID} .cm-lobby-howto h2, #${ROOT_ID} .cm-lobby-leaderboard h2 {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 36px;
      letter-spacing: 4px;
      margin: 0 0 20px;
      color: var(--sg-secondary, #06B6D4);
    }
    #${ROOT_ID} ol {
      list-style: none;
      padding: 0;
      margin: 0;
      counter-reset: cm-howto;
    }
    #${ROOT_ID} .cm-lobby-howto ol li {
      counter-increment: cm-howto;
      font-size: 24px;
      padding: 12px 0 12px 50px;
      color: rgba(255, 255, 255, 0.9);
      position: relative;
    }
    #${ROOT_ID} .cm-lobby-howto ol li::before {
      content: counter(cm-howto);
      position: absolute;
      left: 0;
      top: 8px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--sg-primary, #A855F7);
      color: #fff;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #${ROOT_ID} code {
      /* Bordered cyan chip with WHITE text — matches the round HUD example
         chip for visual parity. Previously had a purple-tinted background +
         cyan text which read as purple on the dark stage. */
      background: rgba(0, 229, 255, 0.10);
      color: #FFFFFF;
      padding: 2px 8px;
      border-radius: 6px;
      border: 1px solid rgba(0, 229, 255, 0.45);
      font-family: 'JetBrains Mono', 'Fira Code', ui-monospace, monospace;
      font-size: 22px;
      text-shadow: 0 0 10px rgba(0, 229, 255, 0.45);
    }
    #${ROOT_ID} .cm-lb-row {
      display: grid;
      grid-template-columns: 36px 1fr auto;
      align-items: center;
      gap: 16px;
      padding: 10px 0;
      font-size: 22px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }
    #${ROOT_ID} .cm-lb-row:last-child {
      border-bottom: 0;
    }
    #${ROOT_ID} .cm-lb-rank {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 24px;
      color: var(--sg-secondary, #06B6D4);
      text-align: center;
    }
    #${ROOT_ID} .cm-lb-name {
      color: #fff;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    #${ROOT_ID} .cm-lb-pts {
      color: var(--sg-accent, #EC4899);
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }
  `;
  document.head.appendChild(s);
}

function formatPoints(n: number): string {
  return n.toLocaleString('en-US');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
